import React from "react";
import "../../assets/scss/components/generic/pagination.scss";

export interface IPaginationReturn {
  currentPage: number;
  totalRecords: Array<any>;
  filteredRecords: Array<any>;
}

export interface IPaginationProps {
  totalRecords: Array<any>; // the array of records to be paginated
  pageLimit: number; // the number of records to be shown per page
  onPageChange: (paginationData: IPaginationReturn) => void;
}

export interface IPaginationState {
  currentPage: number;
  //pages: Array<any>;
}

/**
 * Helper method for creating a range of numbers
 * range(1, 5) => [1, 2, 3, 4, 5]
 */
const range = (from: number, to: number, step = 1) => {
  let i = from;
  const range = [];

  while (i <= to) {
    range.push(i);
    i += step;
  }

  return range;
};

class Pagination extends React.Component<IPaginationProps, IPaginationState> {
  totalRecords: Array<any>; // the array of records to be paginated
  filteredRecords: Array<any>; // the array of records to be paginated
  pageLimit: number; // the number of records to be shown per page
  pageNeighbours = 2; // the number of additional page numbers to show on each side of the current page
  totalPages: number;

  constructor(props: IPaginationProps) {
    super(props);

    // calculate/setup the static values for the pagination
    this.pageLimit = props.pageLimit;
    this.totalRecords = props.totalRecords;

    // set filteredRecords initially as elements from 0th index to pageLimit index
    this.filteredRecords = props.totalRecords.slice(0, props.pageLimit);

    // total number of pages to be shown
    this.totalPages = Math.ceil(
      this.props.totalRecords.length / this.pageLimit
    );

    // default state = page number should be 1s
    this.state = {
      currentPage: 1,
    };
  }

  componentDidMount() {
    this.gotoPage(1);
  }

  componentDidUpdate(prevProps: any) {
    // Typical usage (don't forget to compare props):
    if (prevProps.totalRecords !== this.props.totalRecords) {
      this.totalRecords = this.props.totalRecords;
      this.totalPages = Math.floor(
        this.props.totalRecords.length / this.pageLimit
      );
      this.gotoPage(1);
    }
  }
  fetchPageNumbers = () => {
    const totalPages = Math.ceil(
      this.props.totalRecords.length / this.pageLimit
    );
    const currentPage = this.state.currentPage;
    const pageNeighbours = this.pageNeighbours;

    /**
     * totalNumbers: the total page numbers to show on the control
     * totalBlocks: totalNumbers + 2 to cover for the Prev(<) and Next(>) controls
     */
    const totalNumbers = this.pageNeighbours * 2 + 1;
    const totalBlocks = totalNumbers + 2;

    if (totalPages > totalBlocks) {
      const startPage = Math.max(1, currentPage - pageNeighbours);
      const endPage = Math.min(totalPages, currentPage + pageNeighbours);

      let pages: Array<number | string> = range(startPage, endPage);

      /**
       * hasLeftSpill: has hidden pages to the left
       * hasRightSpill: has hidden pages to the right
       * spillOffset: number of pages left either to the left or to the right
       *              used when starting two or last two pages are selected
       */
      const hasLeftSpill = startPage + 1 > pageNeighbours;
      const hasRightSpill = totalPages - endPage > 0;
      const spillOffset = totalNumbers - pages.length;

      switch (true) {
        // handle: <PREV {6 7} [8] {9 10}
        case hasLeftSpill && !hasRightSpill: {
          const extraPages = range(startPage - spillOffset, startPage - 1);
          return ["PREV", ...extraPages, ...pages];
        }

        // handle: {1 2} [3] {4 5} NEXT>
        case !hasLeftSpill && hasRightSpill: {
          const extraPages = range(endPage + 1, endPage + spillOffset);
          return [...pages, ...extraPages, "NEXT"];
        }

        // handle: <PREV {4 5} [6] {7 8} NEXT>
        case hasLeftSpill && hasRightSpill:
        default: {
          return ["PREV", ...pages, "NEXT"];
        }
      }
    }

    return range(1, totalPages);
  };

  gotoPage = (page: number) => {
    const { onPageChange } = this.props;

    const currentPage = page;

    // Create the object to pass in onPageChange
    const paginationData: IPaginationReturn = {
      currentPage,
      totalRecords: this.props.totalRecords,
      filteredRecords: this.totalRecords.slice(
        (currentPage - 1) * this.pageLimit,
        currentPage * this.pageLimit
      ),
    };

    // After setting the state, execute the onPageChange function and pass in onPageChange
    this.setState({ currentPage }, () => onPageChange(paginationData));
  };

  render() {
    if (this.totalRecords.length === 0 || this.totalPages === 1) return null;
    const { currentPage } = this.state;
    const pages = this.fetchPageNumbers();
    return (
      <>
        <nav className="pagination">
          <ul className="pagination">
            {/* Loop over all the elements in pages */}
            {/* // If element is "PREV"
                // Create the previous button and ellipsis */}
            <React.Fragment>
              <li className="pagination_prev">
                <button
                  className={"pagination_button"}
                  onClick={() => this.gotoPage(1)}
                  disabled={pages[0] !== "PREV"}
                >
                  <span className="edel_icon edel-icon-double-arrow-left"></span>
                  {/* <span>Prev</span> */}
                </button>
              </li>
              <li className="pagination_ellipsis">
                <button
                  className={"pagination_button"}
                  onClick={() => this.gotoPage(currentPage - 1)}
                  disabled={pages[0] !== "PREV"}
                >
                  {<span className="edel_icon edel-icon-arrow-left"></span>}
                </button>
              </li>
            </React.Fragment>
            {pages.map((page: any, index: any) => {
              // Otherwise create normal buttons
              return (
                <>
                  {page !== "PREV" && page !== "NEXT" && (
                    <li key={index} className="pagination_item">
                      <button
                        className={
                          "pagination_button" +
                          (page === currentPage ? " active" : "")
                        }
                        onClick={() => this.gotoPage(page as number)}
                      >
                        {page}
                      </button>
                    </li>
                  )}
                </>
              );
            })}

            {/* // If element is "NEXT"
              // Create the ellipsis and next button */}
            <React.Fragment>
              <li className="pagination_ellipsis">
                <button
                  className={"pagination_button"}
                  onClick={() => this.gotoPage(currentPage + 1)}
                  disabled={pages[pages.length - 1] !== "NEXT"}
                >
                  {<span className="edel_icon edel-icon-arrow-right"></span>}
                </button>
              </li>
              <li className="pagination_next">
                <button
                  className={"pagination_button"}
                  onClick={() => this.gotoPage(this.totalPages)}
                  disabled={pages[pages.length - 1] !== "NEXT"}
                >
                  {/* <span>Next</span> */}
                  <span className="edel_icon edel-icon-double-arrow-right"></span>
                </button>
              </li>
            </React.Fragment>
          </ul>
        </nav>
      </>
    );
  }
}

export default Pagination;
